// frontend/src/services/api.ts
import axios, { AxiosResponse } from 'axios';

// Типы данных
export interface ClinicGalleryImage {
  id: number;
  image: string;
  alt_text: string;
  order: number;
}

export interface ClinicInfo {
  id: number;
  name: string;
  description: string;
  mission: string;
  vision: string;
  privacy_policy?: string;
  terms_of_use?: string;
  address: string;
  phones: string[];
  email: string;
  website: string;
  facebook_url: string;
  instagram_url: string;
  linkedin_url: string;
  working_hours: string;
  logo: string;
  entrance_photo: string;
  gallery_images: ClinicGalleryImage[];
  latitude: number | null;
  longitude: number | null;
  is_active: boolean;
  queue_widget_url?: string;
}

export interface ClinicWorkingDay {
  date: string;
  day_type: 'workday' | 'short' | 'holiday';
  is_working: boolean;
  start_time: string | null;
  end_time: string | null;
  holiday_name: string;
  weekday: number;
}

export interface ClinicScheduleParams {
  year?: number;
  month?: number;
  from?: string;
  to?: string;
}

export interface ClinicDefaultWeekDay {
  day_of_week: number;
  is_working: boolean;
  start_time: string | null;
  end_time: string | null;
}

export interface RelatedService {
  id: number;
  name: string;
  slug: string;
  price: number | null;
  price_note?: string;
  duration_minutes?: number | null;
  department_name?: string;
  is_online?: boolean;
}

export interface ParentContainer {
  id: number;
  slug: string;
  name: string;
  price: number | null;
  department_name: string | null;
}

export interface Service {
  id: number;
  name: string;
  slug: string;
  description?: string;
  short_description: string;
  department?: Department;
  department_name?: string;
  price: number | null;
  price_note: string;
  duration_minutes: number | null;
  image: string;
  order: number;
  related_services?: RelatedService[];
  parent_container?: ParentContainer | null;
  is_online?: boolean;
}

export interface Department {
  id: number;
  name: string;
  slug: string;
  description?: string;
  short_description: string;
  image: string;
  icon: string;
  order: number;
  services_count?: number;
  doctors_count?: number;
  services?: Service[]; // Исправлено: убрали any и добавили правильную типизацию
}

/** Как записаться к врачу — решает сервер (clinic/booking_availability.py). */
export type BookingKind = 'slots' | 'walkin' | 'phone';

export interface DoctorBooking {
  kind: BookingKind;
  /** slots — первый день со свободным слотом; walkin/phone — ближайший рабочий день */
  nearest_date: string | null;
  schedule: Array<{ day_of_week: number; start_time: string; end_time: string }>;
}

export interface Doctor {
  id: number;
  user_id?: number | null; // ID of authentication user for schedule queries
  first_name: string;
  last_name: string;
  middle_name?: string;
  full_name: string;
  slug: string;
  specialization: string;
  degree: string;
  experience_years: number;
  bio?: string;
  achievements?: string;
  departments?: Department[];
  departments_names?: string[];
  services?: Array<{
    id: number;
    name: string;
    slug: string;
    price: number | null;
    duration_minutes?: number | null;
    department_name?: string | null;
    related_services?: Array<{id: number; name: string; slug: string; price: number | null; department_name?: string | null}>;
  }>;
  email?: string;
  phone?: string;
  photo: string;
  order: number;
  booking_mode: 'online' | 'phone' | 'walkin';
  show_queue_widget?: boolean;
  default_schedule?: Array<{ day_of_week: number; start_time: string; end_time: string }>;
  nearest_walkin_date?: string | null;
  booking: DoctorBooking;
}

export interface RoomDoctorDepartment {
  id: number;
  name: string;
  icon: string;
}

export interface RoomDoctor {
  id: number;
  slug: string;
  full_name: string;
  specialization: string;
  degree: string;
  photo: string;
  departments: RoomDoctorDepartment[];
}

export interface Room {
  id: number;
  number: string;
  floor: string;
  floor_display: string;
  name: string;
  svg_x: number | null;
  svg_y: number | null;
  svg_width: number | null;
  svg_height: number | null;
  room_type: 'room' | 'service' | 'hallway' | 'building';
  door: '' | 'N' | 'S' | 'W' | 'E';
  opens_queue_widget?: boolean;
  doctors: RoomDoctor[];
  order: number;
  svg_icon: string;
}

export interface DoctorWeeklySchedule {
  has_schedule: boolean;
  text_schedule?: string | null;
  weekly_schedule?: Array<{
    day_of_week: number;
    day_name: string;
    is_working: boolean;
    start_time: string | null;
    end_time: string | null;
  }>;
  /** Dates (YYYY-MM-DD) in the next 14 days when the clinic is closed */
  clinic_closed_dates?: string[];
}

export interface ContactMessage {
  name: string;
  email: string;
  phone?: string;
  subject: 'review' | 'complaint' | 'suggestion' | 'other';
  message: string;
  website?: string; // honeypot
}

export interface AppointmentRequest {
  patient_first_name: string;
  patient_last_name: string;
  patient_phone: string;
  patient_email?: string;
  doctor?: number;
  department?: number;
  services?: number[];
  service_notes?: string;
  preferred_date?: string;
  preferred_time?: string;
  notes?: string;
}

export interface AppointmentRequestResponse {
  message: string;
  data: {
    id: number;
    patient_first_name: string;
    patient_last_name: string;
    patient_phone: string;
    patient_email?: string;
    doctor?: number;
    doctor_name?: string;
    department?: number;
    department_name?: string;
    services: number[];
    services_list?: Service[];
    service_notes: string;
    preferred_date?: string;
    preferred_time?: string;
    notes?: string;
    status: string;
    has_conflict?: boolean;
    created_at: string;
  };
}

export interface AnyDoctorSlots {
  dates: string[];
  date: string | null;
  doctors: Array<{
    doctor_id: number;
    doctor_slug: string;
    doctor_user_id: number;
    doctor_name: string;
    photo: string | null;
    slots: Array<{ id: number; start_time: string; end_time: string; is_available: boolean }>;
  }>;
}

export interface AvailableSlot {
  id: number;
  start_time: string;
  end_time: string;
  is_available: boolean;
  doctor: number;
  date: string;
}

// Дополнительный интерфейс для прайс-листа
export interface PriceListDepartment extends Department {
  services: Service[]; // Обязательное поле для прайс-листа
}

// Search-related interfaces
export interface Symptom {
  id: number;
  name: string;
  slug: string;
  description?: string;
  departments: Department[];
  is_common: boolean;
}

export interface AutocompleteResult {
  type: 'symptom' | 'department' | 'service' | 'doctor';
  id: number;
  name: string;
  slug: string;
  extra?: {
    departments?: Array<{id: number; name: string; slug: string}>;
    price?: number | null;
    department?: string;
    specialization?: string;
    is_online?: boolean;
  };
}

export interface UnifiedSearchResult {
  query: string;
  symptoms: Symptom[];
  departments: Department[];
  services: Service[];
  doctors: Doctor[];
  total: number;
}

export interface AutocompleteResponse {
  query: string;
  results: AutocompleteResult[];
  total: number;
}

export interface ApiResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Базовая конфигурация API
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Язык для заголовка Accept-Language.
 *
 * Выбор пользователя из localStorage, а при его отсутствии — язык
 * браузера. Пустой localStorage не редкость: гость приходит по ссылке
 * впервые и переключателя в шапке не касался, — и без запасного пути ему
 * доставался бы русский независимо от настроек браузера.
 *
 * Экспортируется, потому что вызовы телемедицины идут голым axios мимо
 * этого интерцептора и должны выбирать язык тем же правилом.
 */
export const currentLanguage = (): string => {
  const stored = localStorage.getItem('elitmed-language');
  if (stored) return stored;
  const browser = navigator.language.toLowerCase();
  if (browser.startsWith('hy')) return 'hy';
  if (browser.startsWith('en')) return 'en';
  return 'ru';
};

// Интерсепторы для обработки запросов и ответов
apiClient.interceptors.request.use(
  (config) => {
    config.headers['Accept-Language'] = currentLanguage();

    // Add authorization token for authenticated requests.
    // Приоритет — токену персонала (сотрудники работают с админкой);
    // если его нет, подставляем токен пациента, чтобы записи,
    // созданные из личного кабинета, привязывались к его аккаунту.
    //
    // Исключение — создание заявки на приём: там приоритет обратный.
    // Заявка привязывается к тому, чьим токеном пришла, и с обычным
    // порядком запись, оформленная пациентом на компьютере с открытой
    // админкой, уходила от имени сотрудника — выходила ничейной, и
    // пациент не видел её в кабинете. Для телеконсультации это хуже
    // вдвойне: ссылку на комнату некуда положить.
    const isBooking =
      config.method?.toLowerCase() === 'post' &&
      (config.url ?? '').includes('/appointment-requests/') &&
      !(config.url ?? '').includes('verify') &&
      !(config.url ?? '').includes('send-verification');

    const staffToken = localStorage.getItem('access_token');
    const patientToken = localStorage.getItem('patient_access_token');
    const token = isBooking
      ? patientToken || staffToken
      : staffToken || patientToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Флаг для предотвращения бесконечных попыток refresh
let isRefreshing = false;

const STAFF_LOGIN_PATH = '/admin-panel/login';

/**
 * Чистит токены персонала и уводит на вход.
 *
 * Перенаправление пропускается, если пользователь уже на странице входа:
 * иначе она перезагружается, снова шлёт запрос с тем же протухшим токеном,
 * получает 401 — и так по кругу. Пользователь видит вечно мигающую страницу
 * вместо формы входа.
 */
const forceStaffLogout = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  if (window.location.pathname !== STAFF_LOGIN_PATH) {
    window.location.href = STAFF_LOGIN_PATH;
  }
};
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Если ошибка 401 и это не повторный запрос
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Если уже идет процесс refresh, добавляем запрос в очередь
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        // Нет refresh токена - выходим
        isRefreshing = false;
        forceStaffLogout();
        return Promise.reject(error);
      }

      try {
        // Попытка обновить токен
        const response = await axios.post(`${API_BASE_URL}/auth/refresh/`, {
          refresh: refreshToken
        });

        const newAccessToken = response.data.access;
        localStorage.setItem('access_token', newAccessToken);

        // Обновляем заголовок Authorization для оригинального запроса
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        processQueue(null, newAccessToken);
        isRefreshing = false;

        // Повторяем оригинальный запрос с новым токеном
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh не удался - выходим
        processQueue(refreshError, null);
        isRefreshing = false;

        forceStaffLogout();

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export interface PreparationList {
  id: number;
  title: string;
  slug: string;
  short_description: string;
  department_name: string | null;
  department_slug: string | null;
  order: number;
}

export interface PreparationDetail extends PreparationList {
  content: string;
}

export interface MedicalNewsCategory {
  id: number;
  name: string;
  slug: string;
}

export interface MedicalNewsList {
  id: number;
  title: string;
  slug: string;
  preview: string;
  image?: string;
  published_at?: string;
  source_url?: string;
  category_name?: string | null;
  category_slug?: string | null;
}

export interface MedicalNewsDetail {
  id: number;
  title: string;
  slug: string;
  content: string;
  image?: string;
  published_at?: string;
  source_url?: string;
  created_at: string;
  category_name?: string | null;
  category_slug?: string | null;
}

export interface CognitiveBiasType {
  id: number;
  name: string;
  slug: string;
  color: string;
}

export interface CognitiveBiasList {
  id: number;
  title: string;
  slug: string;
  bias_type: CognitiveBiasType | null;
  image: string | null;
  preview: string;
}

export interface CognitiveBiasDetail {
  id: number;
  title: string;
  slug: string;
  bias_type: CognitiveBiasType | null;
  image: string | null;
  content: string;
  example?: string;
  author?: string;
  source_url?: string;
  danger_level?: string | null;
  created_at: string;
}

export interface IcdChapter {
  id: number;
  number: number;
  code_range: string;
  name: string;
}

export interface IcdCode {
  id: number;
  code: string;
  name: string;
  name_en: string;
  block_code_range: string;
  block_name: string;
  chapter_number: number;
  chapter_code_range: string;
  chapter_name: string;
}

// --- МКБ-11 (WHO JSON-LD) ---

export interface Icd11LocalizedString {
  '@value': string;
  '@language': string;
}

export type Icd11Title = Icd11LocalizedString | string;

export interface Icd11RootResponse {
  '@context': string;
  '@id': string;
  title: Icd11Title;
  child: string[];  // массив URI дочерних узлов
}

export interface Icd11Entity {
  '@id': string;
  code?: string;
  title: Icd11Title;
  child?: string[];
  parent?: string[];
  definition?: Icd11Title;
  inclusion?: Array<{ label: Icd11Title }>;
  exclusion?: Array<{ label: Icd11Title; foundationReference?: string }>;
  indexTerm?: Array<{ label: Icd11Title; foundationReference?: string }>;
  browserUrl?: string;
  classKind?: string;
}

export interface Icd11SearchResult {
  id: string;
  title: string;
  stemId: string;
  isLeaf: boolean;
  theCode: string;
  score: number;
  titleIsASearchResult: boolean;
  matchingPVs: Array<{ propertyId: string; label: string; score: number; important: boolean }>;
  entity?: string[];
  descendants?: Icd11SearchResult[];
  chapter?: string;
  isResidualOther?: boolean;
  isResidualUnspecified?: boolean;
}

export interface Icd11SearchResponse {
  words: string[];
  destinationEntities: Icd11SearchResult[];
  error: boolean;
  errorMessage: string;
  resultChopped: boolean;
  guessType: number;
  uniqueSearchId: string;
}

/** Извлечь читаемую строку из JSON-LD поля title */
export const getIcd11TitleString = (title: Icd11Title | undefined): string => {
  if (!title) return '';
  if (typeof title === 'string') return title;
  return (title as Icd11LocalizedString)['@value'] ?? '';
};

/** Извлечь path после /mms/ из URI.
 *  "https://id.who.int/.../mms/731629989/unspecified" → "731629989/unspecified"
 *  "https://id.who.int/.../mms/257068234" → "257068234"
 */
export const extractIcd11EntityId = (uri: string): string => {
  const match = uri.match(/\/mms\/(.+)$/);
  return match ? match[1] : uri;
};

export interface FaqCategory {
  id: number;
  name: string;
  order: number;
}

export interface FaqItem {
  id: number;
  category_id: number | null;
  category_name: string | null;
  question: string;
  answer: string;
  order: number;
}

// API сервисы
export class ApiService {
  // Информация о клинике
  static async getClinicInfo(): Promise<ClinicInfo[]> {
    const response: AxiosResponse<ApiResponse<ClinicInfo>> = await apiClient.get('/clinic/');
    return response.data.results;
  }

  static async getClinicSchedule(params: ClinicScheduleParams = {}): Promise<ClinicWorkingDay[]> {
    const response: AxiosResponse<ClinicWorkingDay[]> = await apiClient.get('/clinic-schedule/', { params });
    return response.data;
  }

  static async getClinicDefaultSchedule(): Promise<ClinicDefaultWeekDay[]> {
    const response: AxiosResponse<ClinicDefaultWeekDay[]> = await apiClient.get('/clinic-default-schedule/');
    return response.data;
  }

  static async getClinicClosedDates(days = 30): Promise<string[]> {
    const response: AxiosResponse<{ closed_dates: string[] }> = await apiClient.get(
      `/clinic-default-schedule/closed-dates/?days=${days}`
    );
    return response.data.closed_dates;
  }

  // Отделения
  static async getDepartments(): Promise<Department[]> {
    const response: AxiosResponse<Department[]> = await apiClient.get('/departments/');
    return response.data;
  }

  static async getDepartment(slug: string): Promise<Department> {
    const response: AxiosResponse<Department> = await apiClient.get(`/departments/${slug}/`);
    return response.data;
  }

  static async getDepartmentDoctors(slug: string): Promise<Doctor[]> {
    const response: AxiosResponse<Doctor[]> = await apiClient.get(`/departments/${slug}/doctors/`);
    return response.data;
  }

  static async getDepartmentServices(slug: string, page = 1, pageSize = 20): Promise<ApiResponse<Service>> {
    const response: AxiosResponse<ApiResponse<Service>> = await apiClient.get(`/departments/${slug}/services/`, {
      params: { page, page_size: pageSize },
    });
    return response.data;
  }

  static async searchServices(search: string, page = 1, pageSize = 20): Promise<ApiResponse<Service>> {
    const response: AxiosResponse<ApiResponse<Service>> = await apiClient.get('/services/', {
      params: { search, page, page_size: pageSize },
    });
    return response.data;
  }

  // Врачи
  static async getDoctors(params?: {
    departments__slug?: string;
    specialization?: string;
    search?: string;
  }): Promise<Doctor[]> {
    const response: AxiosResponse<ApiResponse<Doctor>> = await apiClient.get('/doctors/', { params });
    return response.data.results;
  }

  static async getDoctorsPaged(params: {
    departments__slug?: string;
    specialization?: string;
    search?: string;
    page?: number;
    page_size?: number;
  }): Promise<ApiResponse<Doctor>> {
    const response: AxiosResponse<ApiResponse<Doctor>> = await apiClient.get('/doctors/', { params });
    return response.data;
  }

  static async getDoctor(slug: string): Promise<Doctor> {
    const response: AxiosResponse<Doctor> = await apiClient.get(`/doctors/${slug}/`);
    return response.data;
  }

  static async getDoctorWeeklySchedule(slug: string): Promise<DoctorWeeklySchedule> {
    const response: AxiosResponse<DoctorWeeklySchedule> = await apiClient.get(`/doctors/${slug}/schedule/`);
    return response.data;
  }

  // Услуги
  static async getServices(params?: {
    department__slug?: string;
    search?: string;
  }): Promise<Service[]> {
    const response: AxiosResponse<ApiResponse<Service>> = await apiClient.get('/services/', { params });
    return response.data.results;
  }

  static async getService(slug: string): Promise<Service> {
    const response: AxiosResponse<Service> = await apiClient.get(`/services/${slug}/`);
    return response.data;
  }

  // Врачи, выполняющие услугу
  static async getServiceDoctors(slug: string): Promise<Doctor[]> {
    const response: AxiosResponse<Doctor[]> = await apiClient.get(`/services/${slug}/doctors/`);
    return response.data;
  }

  // Популярные услуги
  static async getFeaturedServices(): Promise<Service[]> {
    const response: AxiosResponse<Service[]> = await apiClient.get('/services/featured/');
    return response.data;
  }

  // Прайс-лист
  static async getPriceList(): Promise<PriceListDepartment[]> {
    const response: AxiosResponse<ApiResponse<PriceListDepartment>> = await apiClient.get('/price-list/');
    return response.data.results;
  }

  // Обратная связь
  static async sendContactMessage(message: ContactMessage): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await apiClient.post('/contact/', message);
    return response.data;
  }

  // Заявки на запись на прием
  static async createAppointmentRequest(request: AppointmentRequest): Promise<AppointmentRequestResponse> {
    const response: AxiosResponse<AppointmentRequestResponse> = await apiClient.post('/appointment-requests/', request);
    return response.data;
  }

  // Отправить код верификации
  static async sendVerificationCode(data: {
    phone: string;
    email?: string;
    method?: 'sms' | 'email';
  }): Promise<{
    message: string;
    method_used?: 'sms' | 'email';
    code?: string;
    messengers?: {
      viber?: string;
      whatsapp?: string;
      telegram?: string;
      message?: string;
    };
  }> {
    const response: AxiosResponse<{
      message: string;
      method_used?: 'sms' | 'email';
      code?: string;
      messengers?: {
        viber?: string;
        whatsapp?: string;
        telegram?: string;
        message?: string;
      };
    }> = await apiClient.post(
      '/appointment-requests/send-verification/',
      data
    );
    return response.data;
  }

  // Проверить код верификации
  static async verifyCode(data: {
    phone: string;
    code: string;
  }): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await apiClient.post(
      '/appointment-requests/verify-code/',
      data
    );
    return response.data;
  }

  // Верификация через Google OAuth
  static async verifyGoogleToken(data: { access_token: string }) {
    const response = await apiClient.post('/appointment-requests/verify-google/', data);
    return response.data as { email: string; first_name: string; last_name: string; verified: boolean };
  }

  // Получить список заявок на прием (для админов)
  static async getAppointmentRequests(params?: {
    status?: string;
    doctor?: number;
    department?: number;
  }): Promise<AppointmentRequestResponse['data'][]> {
    const response = await apiClient.get('/appointment-requests/', { params });
    return response.data.results || response.data;
  }

  // Получить статистику по заявкам
  static async getAppointmentRequestsStats(): Promise<{
    total: number;
    new: number;
    contacted: number;
    confirmed: number;
    cancelled: number;
  }> {
    const response = await apiClient.get('/appointment-requests/stats/');
    return response.data;
  }

  // Обновить статус заявки
  static async updateAppointmentRequest(id: number, data: Partial<AppointmentRequest & { status?: string }>): Promise<AppointmentRequestResponse['data']> {
    const response = await apiClient.patch(`/appointment-requests/${id}/`, data);
    return response.data;
  }

  // Получить доступные слоты врача на дату
  static async getAnyDoctorSlots(serviceSlug: string, date: string | null): Promise<AnyDoctorSlots> {
    const response = await apiClient.get<AnyDoctorSlots>(`/services/${serviceSlug}/any-doctor-slots/`, {
      params: date ? { date } : {},
    });
    return response.data;
  }

  static async getAvailableSlots(doctorId: number, date: string): Promise<{ slots: AvailableSlot[]; break_time: string | null }> {
    const response = await apiClient.get<{ slots: AvailableSlot[]; break_time: string | null }>('/auth/schedules/available-slots/', {
      params: { doctor_id: doctorId, date }
    });
    return { slots: response.data.slots || [], break_time: response.data.break_time || null };
  }

  // Search
  static async search(query: string): Promise<UnifiedSearchResult> {
    const response: AxiosResponse<UnifiedSearchResult> = await apiClient.get('/search/search/', {
      params: { q: query }
    });
    return response.data;
  }

  static async autocomplete(query: string): Promise<AutocompleteResponse> {
    const response: AxiosResponse<AutocompleteResponse> = await apiClient.get('/search/search/', {
      params: { q: query, autocomplete: 'true' }
    });
    return response.data;
  }

  // Подготовка к процедурам
  static async getPreparations(params?: {
    department__slug?: string;
    search?: string;
  }): Promise<PreparationList[]> {
    const response: AxiosResponse<ApiResponse<PreparationList>> = await apiClient.get('/preparations/', { params });
    return response.data.results;
  }

  static async getPreparation(slug: string): Promise<PreparationDetail> {
    const response: AxiosResponse<PreparationDetail> = await apiClient.get(`/preparations/${slug}/`);
    return response.data;
  }

  static async getFaqCategories(): Promise<FaqCategory[]> {
    const response: AxiosResponse<FaqCategory[]> = await apiClient.get('/faq/categories/');
    return response.data;
  }

  static async getFaqItems(params?: { search?: string; category?: number | string }): Promise<FaqItem[]> {
    const response: AxiosResponse<FaqItem[]> = await apiClient.get('/faq/items/', { params });
    return response.data;
  }

  static async getMedicalNewsCategories(): Promise<MedicalNewsCategory[]> {
    const response: AxiosResponse<MedicalNewsCategory[]> = await apiClient.get('/medical-news/categories/');
    return response.data;
  }

  static async getMedicalNewsList(params?: { search?: string; category__slug?: string }): Promise<MedicalNewsList[]> {
    const response: AxiosResponse<ApiResponse<MedicalNewsList>> = await apiClient.get('/medical-news/', { params });
    return response.data.results;
  }

  static async getMedicalNews(slug: string): Promise<MedicalNewsDetail> {
    const response: AxiosResponse<MedicalNewsDetail> = await apiClient.get(`/medical-news/${slug}/`);
    return response.data;
  }

  static async getCognitiveBiasTypes(): Promise<CognitiveBiasType[]> {
    const response: AxiosResponse<CognitiveBiasType[]> = await apiClient.get('/cognitive-bias-types/');
    return response.data;
  }

  static async getCognitiveBiases(params?: { search?: string; bias_type__slug?: string }): Promise<CognitiveBiasList[]> {
    const response: AxiosResponse<CognitiveBiasList[]> = await apiClient.get('/cognitive-biases/', { params });
    return response.data;
  }

  static async getCognitiveBias(slug: string): Promise<CognitiveBiasDetail> {
    const response: AxiosResponse<CognitiveBiasDetail> = await apiClient.get(`/cognitive-biases/${slug}/`);
    return response.data;
  }

  static async getRooms(floor?: string): Promise<Room[]> {
    const params = floor ? { floor } : {};
    const response: AxiosResponse<Room[]> = await apiClient.get('/rooms/', { params });
    return response.data;
  }

  static async getIcdChapters(): Promise<IcdChapter[]> {
    const response: AxiosResponse<IcdChapter[]> = await apiClient.get('/icd/chapters/');
    return response.data;
  }

  static async getIcdCodes(params: {
    search?: string;
    chapter?: number | string;
    page?: number;
    page_size?: number;
  }): Promise<ApiResponse<IcdCode>> {
    const response: AxiosResponse<ApiResponse<IcdCode>> = await apiClient.get('/icd/codes/', { params });
    return response.data;
  }

  // --- МКБ-11 ---
  static async getIcd11Chapters(lang = 'ru'): Promise<Icd11RootResponse> {
    const response = await apiClient.get('/icd11/chapters/', { params: { lang } });
    return response.data;
  }

  static async getIcd11Entity(entityId: string, lang = 'ru'): Promise<Icd11Entity> {
    const response = await apiClient.get(`/icd11/entity/${entityId}/`, { params: { lang } });
    return response.data;
  }

  static async searchIcd11(q: string, lang = 'ru'): Promise<Icd11SearchResponse> {
    const response = await apiClient.get('/icd11/search/', { params: { q, lang } });
    return response.data;
  }
}

// React Query hooks
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from 'react-query';

// Hooks для работы с API через React Query
export const useClinicInfo = () => {
  return useQuery('clinicInfo', ApiService.getClinicInfo, {
    staleTime: 10 * 60 * 1000, // 10 минут
  });
};

export const useClinicSchedule = (params: ClinicScheduleParams = {}) => {
  return useQuery(
    ['clinicSchedule', params],
    () => ApiService.getClinicSchedule(params),
    {
      staleTime: 10 * 60 * 1000,
      keepPreviousData: true,
    }
  );
};

export const useClinicDefaultSchedule = () => {
  return useQuery('clinicDefaultSchedule', ApiService.getClinicDefaultSchedule, {
    staleTime: 30 * 60 * 1000, // 30 минут — меняется редко
  });
};

export const useClinicClosedDates = (days = 30) => {
  return useQuery(['clinicClosedDates', days], () => ApiService.getClinicClosedDates(days), {
    staleTime: 30 * 60 * 1000,
  });
};

export const useDepartments = () => {
  return useQuery('departments', ApiService.getDepartments, {
    staleTime: 5 * 60 * 1000, // 5 минут
  });
};

export const useDepartment = (slug: string) => {
  return useQuery(['department', slug], () => ApiService.getDepartment(slug), {
    enabled: !!slug,
  });
};

export const useDepartmentDoctors = (slug: string) => {
  return useQuery(
    ['departmentDoctors', slug],
    () => ApiService.getDepartmentDoctors(slug),
    {
      enabled: !!slug,
    }
  );
};

export const useInfiniteDepartmentServices = (slug: string, pageSize = 20) => {
  return useInfiniteQuery(
    ['departmentServices', slug],
    ({ pageParam = 1 }) => ApiService.getDepartmentServices(slug, pageParam, pageSize),
    {
      enabled: !!slug,
      getNextPageParam: (lastPage, pages) => {
        return lastPage.next ? pages.length + 1 : undefined;
      },
    }
  );
};

export const useInfiniteServiceSearch = (searchTerm: string, pageSize = 20) => {
  return useInfiniteQuery(
    ['serviceSearch', searchTerm],
    ({ pageParam = 1 }) => ApiService.searchServices(searchTerm, pageParam, pageSize),
    {
      enabled: searchTerm.trim().length >= 2,
      getNextPageParam: (lastPage, pages) => {
        return lastPage.next ? pages.length + 1 : undefined;
      },
      keepPreviousData: true,
    }
  );
};

export const useDoctors = (params?: {
  departments__slug?: string;
  specialization?: string;
  search?: string;
}) => {
  return useQuery(['doctors', params], () => ApiService.getDoctors(params), {
    staleTime: 5 * 60 * 1000,
    keepPreviousData: true,
  });
};

export const useInfiniteDoctors = (
  params: { departments__slug?: string; specialization?: string; search?: string },
  pageSize = 12
) => {
  return useInfiniteQuery(
    ['doctorsInfinite', params],
    ({ pageParam = 1 }) => ApiService.getDoctorsPaged({ ...params, page: pageParam, page_size: pageSize }),
    {
      getNextPageParam: (lastPage, pages) => {
        return lastPage.next ? pages.length + 1 : undefined;
      },
      staleTime: 5 * 60 * 1000,
      keepPreviousData: true,
    }
  );
};

export const useDoctor = (slug: string) => {
  return useQuery(['doctor', slug], () => ApiService.getDoctor(slug), {
    enabled: !!slug,
  });
};

export const useDoctorWeeklySchedule = (slug: string) => {
  return useQuery(['doctor-schedule', slug], () => ApiService.getDoctorWeeklySchedule(slug), {
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
};

export const useServices = (params?: {
  department__slug?: string;
  search?: string;
}) => {
  return useQuery(['services', params], () => ApiService.getServices(params), {
    staleTime: 5 * 60 * 1000,
    keepPreviousData: true, // Keep previous data while loading new data
  });
};

export const useService = (slug: string) => {
  return useQuery(['service', slug], () => ApiService.getService(slug), {
    enabled: !!slug,
  });
};

export const useFeaturedServices = () => {
  return useQuery('featuredServices', ApiService.getFeaturedServices, {
    staleTime: 10 * 60 * 1000, // 10 minutes - featured services don't change often
  });
};

export const usePriceList = () => {
  return useQuery('priceList', ApiService.getPriceList, {
    staleTime: 10 * 60 * 1000,
  });
};

export const useContactMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(ApiService.sendContactMessage, {
    onSuccess: () => {
      // Можно добавить уведомления об успехе
      queryClient.invalidateQueries('contactMessages');
    },
  });
};

export const useCreateAppointmentRequest = () => {
  const queryClient = useQueryClient();

  return useMutation(ApiService.createAppointmentRequest, {
    onSuccess: () => {
      // Можно добавить уведомления об успехе
      queryClient.invalidateQueries('appointmentRequests');
      queryClient.invalidateQueries('appointmentRequestsStats');
      // Список записей в кабинете пациента: без этого он показывал бы
      // ответ, полученный до записи. При staleTime в 5 минут заявка
      // появлялась только после перезагрузки страницы.
      queryClient.invalidateQueries('patient-appointments');
      queryClient.invalidateQueries('my-teleconsultations');
    },
  });
};

// Получить список заявок (для админов)
export const useAppointmentRequests = (params?: {
  status?: string;
  doctor?: number;
  department?: number;
}) => {
  return useQuery(['appointmentRequests', params], () => ApiService.getAppointmentRequests(params), {
    enabled: true,
    staleTime: 30000, // 30 seconds
  });
};

// Получить статистику по заявкам
export const useAppointmentRequestsStats = () => {
  return useQuery('appointmentRequestsStats', ApiService.getAppointmentRequestsStats, {
    staleTime: 30000, // 30 seconds
  });
};

// Обновить заявку
export const useUpdateAppointmentRequest = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, data }: { id: number; data: Partial<AppointmentRequest & { status?: string }> }) =>
      ApiService.updateAppointmentRequest(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('appointmentRequests');
        queryClient.invalidateQueries('appointmentRequestsStats');
      },
    }
  );
};

// Дополнительные хуки для удобства (используются в BookAppointmentPage)
export const useDoctorBySlug = (slug: string) => {
  return useQuery(['doctor', slug], () => ApiService.getDoctor(slug), {
    enabled: !!slug && slug.length > 0,
  });
};

export const useDepartmentBySlug = (slug: string) => {
  return useQuery(['department', slug], () => ApiService.getDepartment(slug), {
    enabled: !!slug && slug.length > 0,
  });
};

export const useServiceBySlug = (slug: string) => {
  return useQuery(['service', slug], () => ApiService.getService(slug), {
    enabled: !!slug && slug.length > 0,
  });
};

export const useServiceDoctors = (slug: string) => {
  return useQuery(['service-doctors', slug], () => ApiService.getServiceDoctors(slug), {
    enabled: !!slug && slug.length > 0,
  });
};

export const useAnyDoctorSlots = (serviceSlug: string, date: string | null, enabled: boolean) =>
  useQuery(
    ['anyDoctorSlots', serviceSlug, date],
    () => ApiService.getAnyDoctorSlots(serviceSlug, date),
    { enabled: enabled && !!serviceSlug, staleTime: 60 * 1000 },
  );

export const useAvailableSlots = (doctorId: number | null, date: string | null) => {
  return useQuery(
    ['availableSlots', doctorId, date],
    () => ApiService.getAvailableSlots(doctorId!, date!),
    {
      enabled: !!doctorId && !!date,
      staleTime: 1 * 60 * 1000, // 1 minute
    }
  );
};

// Search hooks
export const useSearch = (query: string) => {
  return useQuery(
    ['search', query],
    () => ApiService.search(query),
    {
      enabled: query.length >= 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

export const useAutocomplete = (query: string, enabled: boolean = true) => {
  return useQuery(
    ['autocomplete', query],
    () => ApiService.autocomplete(query),
    {
      enabled: enabled && query.length >= 2,
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  );
};

export const useSendVerificationCode = () =>
  useMutation((data: { phone: string; email?: string; method?: 'sms' | 'email' }) =>
    ApiService.sendVerificationCode(data)
  );

export const useVerifyCode = () =>
  useMutation((data: { phone: string; code: string }) =>
    ApiService.verifyCode(data)
  );

export const useVerifyGoogleToken = () =>
  useMutation((data: { access_token: string }) => ApiService.verifyGoogleToken(data));

export const usePreparations = (params?: { department__slug?: string; search?: string }) => {
  return useQuery(
    ['preparations', params],
    () => ApiService.getPreparations(params),
    { staleTime: 5 * 60 * 1000 }
  );
};

export const usePreparation = (slug: string) => {
  return useQuery(['preparation', slug], () => ApiService.getPreparation(slug), {
    enabled: !!slug,
  });
};

export const useMedicalNewsCategories = () => {
  return useQuery('medicalNewsCategories', ApiService.getMedicalNewsCategories, {
    staleTime: 60 * 60 * 1000,
  });
};

export const useMedicalNewsList = (params?: { search?: string; category__slug?: string }) => {
  return useQuery(
    ['medical-news', params],
    () => ApiService.getMedicalNewsList(params),
    { staleTime: 5 * 60 * 1000 }
  );
};

export const useMedicalNews = (slug?: string) => {
  return useQuery(['medical-news', slug], () => ApiService.getMedicalNews(slug!), {
    enabled: !!slug,
  });
};

export const useIcdChapters = () => {
  return useQuery('icdChapters', ApiService.getIcdChapters, {
    staleTime: 60 * 60 * 1000, // 1 час — данные не меняются
  });
};

export const useIcdCodes = (params: {
  search?: string;
  chapter?: number | string;
  page?: number;
  page_size?: number;
}) => {
  return useQuery(
    ['icdCodes', params],
    () => ApiService.getIcdCodes(params),
    {
      staleTime: 5 * 60 * 1000,
      keepPreviousData: true,
    }
  );
};

// Утилиты для работы с изображениями
export const getImageUrl = (imagePath: string): string => {
  if (!imagePath) return '';
  if (imagePath.startsWith('http')) return imagePath;
  return `${API_BASE_URL.replace('/api/v1', '')}${imagePath}`;
};

// Утилиты для форматирования данных
export const formatPrice = (price: number | null, currency = 'AMD'): string => {
  if (!price) return 'По запросу';
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

export const formatDuration = (minutes: number | null): string => {
  if (!minutes) return '';
  if (minutes < 60) return `${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) return `${hours} ч`;
  return `${hours} ч ${remainingMinutes} мин`;
};

export const useFaqCategories = () => {
  return useQuery('faqCategories', ApiService.getFaqCategories, {
    staleTime: 10 * 60 * 1000,
  });
};

export const useFaqItems = (params?: { search?: string; category?: number | string }) => {
  return useQuery(
    ['faqItems', params],
    () => ApiService.getFaqItems(params),
    { staleTime: 5 * 60 * 1000 }
  );
};

export const useCognitiveBiasTypes = () => {
  return useQuery('cognitiveBiasTypes', ApiService.getCognitiveBiasTypes, {
    staleTime: 10 * 60 * 1000,
  });
};

export const useCognitiveBiases = (params?: { search?: string; bias_type__slug?: string }) => {
  return useQuery(
    ['cognitiveBiases', params],
    () => ApiService.getCognitiveBiases(params),
    { staleTime: 5 * 60 * 1000 }
  );
};

export const useCognitiveBias = (slug?: string) => {
  return useQuery(['cognitiveBias', slug], () => ApiService.getCognitiveBias(slug!), {
    enabled: !!slug,
  });
};

export const useRooms = (floor?: string) => {
  return useQuery(['rooms', floor], () => ApiService.getRooms(floor), {
    staleTime: 10 * 60 * 1000,
  });
};

// --- МКБ-11 хуки ---

export const useIcd11Chapters = (lang: string) => {
  return useQuery(
    ['icd11Chapters', lang],
    () => ApiService.getIcd11Chapters(lang),
    { staleTime: 24 * 60 * 60 * 1000 }
  );
};

export const useIcd11Entity = (entityId: string | null, lang: string) => {
  return useQuery(
    ['icd11Entity', entityId, lang],
    () => ApiService.getIcd11Entity(entityId!, lang),
    { enabled: !!entityId, staleTime: 24 * 60 * 60 * 1000 }
  );
};

export const useIcd11Search = (q: string, lang: string) => {
  return useQuery(
    ['icd11Search', q, lang],
    () => ApiService.searchIcd11(q, lang),
    {
      enabled: q.length >= 2,
      staleTime: 5 * 60 * 1000,
      keepPreviousData: true,
    }
  );
};

export interface BookingQuestion {
  id: number;
  text: string;
}

export interface BookingQuestionnaire {
  id: number;
  name: string;
  questions: BookingQuestion[];
}

/**
 * Анкета для выбранной услуги и врача, либо null.
 *
 * 204 означает «анкета не настроена» — штатный случай, а не ошибка:
 * анкета необязательна. Врач может быть не выбран («любой врач»), тогда
 * сервер отдаст анкету услуги.
 */
export const fetchBookingQuestionnaire = async (
  serviceId: number,
  doctorId?: number,
): Promise<BookingQuestionnaire | null> => {
  const params: Record<string, number> = { service: serviceId };
  if (doctorId) params.doctor = doctorId;
  const response = await apiClient.get<BookingQuestionnaire>('/questionnaire/', { params });
  return response.status === 204 ? null : response.data;
};

// Export apiClient for authenticated requests (admin panel, schedule management, etc.)
export { apiClient };

export default ApiService;